/**
 * Penggambar tiap varian orb ke konteks Canvas 2D.
 *
 * Canvas 2D dipilih, bukan WebGL, karena orb dirender pada 22–56 px: biaya
 * tetap sebuah konteks WebGL (VRAM, batas jumlah konteks per halaman) tidak
 * sebanding dengan ~2.000 operasi gambar kecil per frame yang murah di 2D.
 * Lihat ORB-LOGO-PROPOSAL.md §1.1.
 */

import type { OrbVariant } from "@/types/ai-assistant-orb";
import { rgba, sampleRamp } from "./orb-colors";
import { type OrbFrame, type OrbPainter, paramOr } from "./orb-frame";
import {
	effectivePoints,
	fibonacciSphere,
	project,
	rotatePoint,
} from "./orb-geometry";

function drawParticleSphere(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	radius: number,
	frame: OrbFrame,
): void {
	const { params, ramp, time, cssSize, light, brightness } = frame;
	const count = effectivePoints(paramOr(params, "density", 1600), cssSize);
	const points = fibonacciSphere(count);
	const yaw = time * (paramOr(params, "speed", 35) / 100) * 0.6;
	const pitch = (paramOr(params, "tilt", 18) / 180) * Math.PI;
	const dotSize = Math.max(0.6, (radius / 46) * 1.5);
	const baseAlpha = light ? 0.35 : 0.14;

	for (let i = 0; i < count; i++) {
		const rotated = rotatePoint(
			{
				x: points[i * 3] as number,
				y: points[i * 3 + 1] as number,
				z: points[i * 3 + 2] as number,
			},
			yaw,
			pitch,
		);
		const { x, y, depth } = project(rotated, cx, cy, radius);
		// Warna mengikuti ketinggian titik: kutub atas memakai warna awal,
		// kutub bawah warna akhir — inilah transisi vertikal pada referensi.
		const color = sampleRamp(ramp, (1 - rotated.y) / 2);
		ctx.fillStyle = rgba(color, (baseAlpha + depth * 0.8) * brightness);
		const size = dotSize * (0.45 + depth * 0.75);
		ctx.fillRect(x - size / 2, y - size / 2, size, size);
	}
}

function drawWireframeMesh(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	radius: number,
	frame: OrbFrame,
): void {
	const { params, ramp, time, cssSize, light, brightness } = frame;
	const density = paramOr(params, "density", 20);
	// Kanvas kecil tidak sanggup menampilkan 40 garis; kerapatan ikut menyusut.
	const segments = Math.max(
		6,
		Math.round(density * Math.min(1, cssSize / 56 + 0.35)),
	);
	const amplitude = (paramOr(params, "amplitude", 40) / 100) * 0.22;
	const yaw = time * (paramOr(params, "speed", 30) / 100) * 0.5;
	ctx.lineWidth = Math.max(0.4, radius / 60);

	for (let ring = 0; ring <= segments; ring++) {
		const v = (ring / segments) * Math.PI;
		ctx.beginPath();
		for (let step = 0; step <= segments * 2; step++) {
			const u = (step / (segments * 2)) * Math.PI * 2 + yaw;
			// Dua sinus berlapis membuat siluet bergelombang, bukan bulat sempurna.
			const wobble =
				1 +
				amplitude * Math.sin(u * 4 + time * 1.3) * Math.sin(v * 5 - time * 0.9);
			const { x, y } = project(
				{
					x: Math.sin(v) * Math.cos(u) * wobble,
					y: Math.cos(v) * wobble,
					z: Math.sin(v) * Math.sin(u) * wobble,
				},
				cx,
				cy,
				radius,
			);
			step === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
		}
		ctx.strokeStyle = rgba(
			sampleRamp(ramp, ring / segments),
			(light ? 0.55 : 0.42) * brightness,
		);
		ctx.stroke();
	}
}

function drawGlowRing(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	radius: number,
	frame: OrbFrame,
): void {
	const { params, ramp, time, cssSize, brightness } = frame;
	const pulse = 1 + Math.sin(time * (paramOr(params, "speed", 25) / 40)) * 0.06;
	const ringRadius = radius * pulse;
	// Di ukuran mungil cincin dipertebal, kalau tidak ia hilang jadi satu garis rambut.
	const thicknessScale = cssSize < 24 ? 1.6 : 1;
	const thickness =
		Math.max(1, (paramOr(params, "thickness", 6) / 100) * radius * 1.6) *
		thicknessScale;
	const core = paramOr(params, "core", 50) / 100;
	const first = sampleRamp(ramp, 0);
	const last = sampleRamp(ramp, 1);

	const fill = ctx.createRadialGradient(cx, cy, 0, cx, cy, ringRadius);
	fill.addColorStop(0, rgba(first, 0.55 * core * brightness));
	fill.addColorStop(1, rgba(last, 0));
	ctx.fillStyle = fill;
	ctx.beginPath();
	ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
	ctx.fill();

	const stroke = ctx.createLinearGradient(
		cx - radius,
		cy - radius,
		cx + radius,
		cy + radius,
	);
	stroke.addColorStop(0, rgba(first, brightness));
	stroke.addColorStop(1, rgba(last, brightness));
	ctx.strokeStyle = stroke;
	ctx.lineWidth = thickness;
	ctx.beginPath();
	ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
	ctx.stroke();
}

/**
 * Penggambar per varian; menambah varian Canvas 2D = menambah satu entri di
 * sini. Varian WebGL (liquid-glass-*, liquid-morph) TIDAK dirender lewat jalur ini saat
 * WebGL2 tersedia — AssistantOrb mendelegasikannya ke orb-webgl-stage.ts.
 * Entrinya tetap diisi `drawGlowRing` justru karena jalur inilah yang dipakai
 * sebagai FALLBACK saat WebGL2 gagal/hilang: "glow-ring" dipilih sebagai
 * Canvas 2D termurah, bukan sekadar mengisi tipe secara formal.
 */
export const ORB_PAINTERS: Record<OrbVariant, OrbPainter> = {
	"particle-sphere": drawParticleSphere,
	"wireframe-mesh": drawWireframeMesh,
	"glow-ring": drawGlowRing,
	"liquid-glass-flow": drawGlowRing,
	"liquid-glass-dramatic": drawGlowRing,
	"liquid-morph": drawGlowRing,
};
